import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  ArrowLeft, Bug, MessageSquare, AlertTriangle, Lightbulb,
  HelpCircle, Send, Loader2, CheckCircle, Clock, XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { formatLocalDate } from '../utils/dateUtils';

const REPORT_TYPES = [
  { value: 'bug', label: 'Bug Report', icon: Bug, color: 'text-red-500' },
  { value: 'feedback', label: 'Feedback', icon: MessageSquare, color: 'text-blue-500' },
  { value: 'content_issue', label: 'Content Issue', icon: AlertTriangle, color: 'text-amber-500' },
  { value: 'feature_request', label: 'Feature Request', icon: Lightbulb, color: 'text-green-500' },
  { value: 'other', label: 'Other', icon: HelpCircle, color: 'text-gray-500' }
];

const ReportIssue = () => {
  const navigate = useNavigate();
  const { API, token } = useAuth();
  const [myReports, setMyReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  
  const [formData, setFormData] = useState({
    report_type: '',
    title: '',
    description: '',
    page_url: window.location.href
  });

  useEffect(() => {
    fetchMyReports();
  }, []);

  const fetchMyReports = async () => {
    try {
      const response = await axios.get(`${API}/reports/my-reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyReports(response.data.reports || []);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.report_type || !formData.title || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/reports`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Report submitted successfully! Thank you for your feedback.');
      setFormData({
        report_type: '',
        title: '',
        description: '',
        page_url: window.location.href
      });
      setShowForm(false);
      fetchMyReports();
    } catch (error) {
      console.error('Error submitting report:', error);
      toast.error('Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'resolved':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'in_progress':
        return <Clock className="w-4 h-4 text-blue-500" />;
      case 'closed':
        return <XCircle className="w-4 h-4 text-gray-500" />;
      default:
        return <Clock className="w-4 h-4 text-amber-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'resolved': return 'bg-green-100 text-green-700';
      case 'in_progress': return 'bg-blue-100 text-blue-700';
      case 'closed': return 'bg-gray-100 text-gray-600';
      default: return 'bg-amber-100 text-amber-700';
    }
  };

  const formatDate = (dateStr) => {
    return formatLocalDate(dateStr, 'en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-8" data-testid="report-issue">
      <Button 
        onClick={() => navigate('/dashboard')} 
        variant="ghost" 
        className="text-primary hover:text-primary/80"
        data-testid="back-button"
      >
        <ArrowLeft className="w-5 h-5 mr-2" />
        Back to Dashboard
      </Button>

      {/* Header */}
      <div className="bg-gradient-to-br from-slate-500/10 to-gray-500/10 rounded-xl p-8">
        <div className="flex items-center gap-4 mb-2">
          <Bug className="w-10 h-10 text-slate-600" strokeWidth={1.5} />
          <h1 className="text-3xl font-nunito font-bold text-[#2D2D2D]">
            Report an Issue
          </h1>
        </div>
        <p className="text-[#57534E]">
          Help us improve by reporting bugs, suggesting features, or providing feedback
        </p>
      </div>

      {/* New Report Button */}
      {!showForm && (
        <Button
          onClick={() => setShowForm(true)}
          className="w-full bg-slate-600 hover:bg-slate-700 text-white rounded-xl py-6"
          data-testid="new-report-btn"
        >
          <Send className="w-5 h-5 mr-2" />
          Submit New Report
        </Button>
      )}

      {/* Report Form */}
      {showForm && (
        <Card className="p-6 border-2 border-slate-200">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-[#2D2D2D]">New Report</h2>
            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>
              ✕
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Report Type */}
            <div>
              <Label className="text-[#2D2D2D] font-medium mb-3 block">Report Type *</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {REPORT_TYPES.map(({ value, label, icon: Icon, color }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setFormData({ ...formData, report_type: value })}
                    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                      formData.report_type === value
                        ? 'border-slate-500 bg-slate-50'
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                    data-testid={`report-type-${value}`}
                  >
                    <Icon className={`w-6 h-6 ${color}`} />
                    <span className="text-sm font-medium text-[#2D2D2D]">{label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Title */}
            <div>
              <Label className="text-[#2D2D2D] font-medium">Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Brief summary of your report"
                className="mt-1"
                required
                data-testid="report-title-input"
              />
            </div>

            {/* Description */}
            <div>
              <Label className="text-[#2D2D2D] font-medium">Description *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Please provide as much detail as possible. For bugs, include steps to reproduce."
                className="mt-1 min-h-[150px]"
                required
                data-testid="report-description-input"
              />
            </div>

            {/* Page URL */}
            <div>
              <Label className="text-[#2D2D2D] font-medium">Page URL (optional)</Label>
              <Input
                value={formData.page_url}
                onChange={(e) => setFormData({ ...formData, page_url: e.target.value })}
                placeholder="Which page were you on?"
                className="mt-1"
                data-testid="report-url-input"
              />
            </div>

            {/* Submit */}
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowForm(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 bg-slate-600 hover:bg-slate-700 text-white"
                data-testid="submit-report-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit Report
                  </>
                )}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* My Reports */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-[#2D2D2D]">My Reports</h2>
        
        {loading ? (
          <p className="text-center text-[#57534E] py-8">Loading...</p>
        ) : myReports.length === 0 ? (
          <Card className="p-8 text-center">
            <MessageSquare className="w-12 h-12 mx-auto mb-4 text-stone-300" />
            <p className="text-[#57534E]">No reports submitted yet.</p>
          </Card>
        ) : (
          myReports.map((report) => {
            const TypeIcon = REPORT_TYPES.find(t => t.value === report.report_type)?.icon || HelpCircle;
            const typeColor = REPORT_TYPES.find(t => t.value === report.report_type)?.color || 'text-gray-500';
            
            return (
              <Card key={report.id} className="p-5" data-testid={`report-${report.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3">
                    <TypeIcon className={`w-5 h-5 mt-0.5 ${typeColor}`} />
                    <div>
                      <p className="font-bold text-[#2D2D2D]">{report.title}</p>
                      <p className="text-sm text-[#A8A29E]">{formatDate(report.created_at)}</p>
                    </div>
                  </div>
                  
                  <span className={`flex items-center gap-1 text-xs px-3 py-1 rounded-full ${getStatusColor(report.status)}`}>
                    {getStatusIcon(report.status)}
                    {report.status.replace('_', ' ')}
                  </span>
                </div>
                
                <p className="text-sm text-[#57534E] line-clamp-2">{report.description}</p>
                
                {report.admin_notes && (
                  <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm font-medium text-blue-700">Admin Response:</p>
                    <p className="text-sm text-blue-600">{report.admin_notes}</p>
                  </div>
                )}
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ReportIssue;
